import axiosInstance from '../axiosInstance';

class NotificationAPI {
  constructor() {
    this.baseURL = '/push-notifications';
  }

  // ===== FCM Token Management =====

  /**
   * Register FCM token with the backend
   * @param {string} fcmToken - FCM token
   * @param {object} deviceInfo - Device information
   * @param {string} deviceInfo.device_type - Device type (android/ios)
   * @param {string} deviceInfo.device_name - Device name
   * @param {string} deviceInfo.app_version - App version
   * @param {string} deviceInfo.os_version - OS version
   * @returns {Promise} API response
   */
  async registerToken(fcmToken, deviceInfo) {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/register`, {
        fcm_token: fcmToken,
        device_type: deviceInfo.device_type,
        device_name: deviceInfo.device_name,
        app_version: deviceInfo.app_version,
        os_version: deviceInfo.os_version
      });
      
      console.log('Token registered successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error registering token:', error);
      throw error;
    }
  }

  /**
   * Remove FCM token from backend
   * @param {string} fcmToken - FCM token to remove
   * @returns {Promise} API response
   */
  async removeToken(fcmToken) {
    try {
      const response = await axiosInstance.delete(`${this.baseURL}/remove`, {
        data: { fcm_token: fcmToken }
      });
      
      console.log('Token removed successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error removing token:', error);
      throw error;
    }
  }

  // ===== Device Management =====

  /**
   * Get user's registered devices
   * @returns {Promise} API response with devices list
   */
  async getDevices() {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/devices`);
      return response.data;
    } catch (error) {
      console.error('Error getting devices:', error);
      throw error;
    }
  }

  /**
   * Remove a specific device
   * @param {string} deviceId - Device ID to remove
   * @returns {Promise} API response
   */
  async removeDevice(deviceId) {
    try {
      const response = await axiosInstance.delete(`${this.baseURL}/devices/${deviceId}`);
      console.log('Device removed successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error removing device:', error);
      throw error;
    }
  }

  // ===== Notification Settings =====

  /**
   * Get user's notification settings
   * @returns {Promise} API response with settings
   */
  async getSettings() {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/settings`);
      return response.data;
    } catch (error) {
      console.error('Error getting notification settings:', error);
      throw error;
    }
  }

  /**
   * Update user's notification settings
   * @param {object} settings - Notification preferences
   * @param {boolean} settings.appointment_reminders - Enable appointment reminders
   * @param {boolean} settings.payment_receipts - Enable payment receipts
   * @param {boolean} settings.wallet_credits - Enable wallet credit notifications
   * @param {boolean} settings.general_notifications - Enable general notifications
   * @returns {Promise} API response
   */
  async updateSettings(settings) {
    try {
      const response = await axiosInstance.put(`${this.baseURL}/settings`, {
        appointment_reminders: settings.appointment_reminders,
        payment_receipts: settings.payment_receipts,
        wallet_credits: settings.wallet_credits,
        general_notifications: settings.general_notifications
      });
      console.log('Settings updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }

  // ===== Notification Management =====

  /**
   * Get notifications with pagination and filtering
   * @param {object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.per_page - Items per page (default: 15, max: 100)
   * @param {string} params.status - Filter by status: "all" | "read" | "unread" (default: "all")
   * @param {string} params.type - Filter by type: "appointment" | "payment" | "wallet" | "general"
   * @returns {Promise} API response with notifications and pagination meta
   */
  async getNotifications(params = {}) {
    try {
      const queryParams = {
        page: params.page || 1,
        per_page: Math.min(params.per_page || 15, 100),
        status: params.status || 'all',
        ...(params.type && { type: params.type })
      };

      const response = await axiosInstance.get(`${this.baseURL}/notifications`, {
        params: queryParams
      });
      
      return response.data;
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise} API response
   */
  async markAsRead(notificationId) {
    try {
      const response = await axiosInstance.put(`${this.baseURL}/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise} API response
   */
  async markAllAsRead() {
    try {
      const response = await axiosInstance.put(`${this.baseURL}/notifications/mark-all-read`);
      console.log('All notifications marked as read:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete a specific notification
   * @param {string} notificationId - Notification ID to delete
   * @returns {Promise} API response
   */
  async deleteNotification(notificationId) {
    try {
      const response = await axiosInstance.delete(`${this.baseURL}/notifications/${notificationId}`);
      console.log('Notification deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise} API response with count
   */
  async getUnreadCount() {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/notifications/unread-count`);
      return response.data;
    } catch (error) {
      console.error('Error getting unread count:', error);
      throw error;
    }
  }

  // ===== Testing =====

  /**
   * Send a test notification
   * @returns {Promise} API response
   */
  async sendTestNotification() {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/test`);
      
      console.log('Test notification sent:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }

  // ===== Legacy Support (Deprecated) =====

  /**
   * @deprecated Use getNotifications() instead
   * Get notification history
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise} API response with notification history
   */
  async getHistory(page = 1, limit = 20) {
    console.warn('getHistory() is deprecated. Use getNotifications() instead.');
    return this.getNotifications({ page, per_page: limit });
  }

  /**
   * @deprecated Use registerToken() instead
   * Update existing FCM token
   * @param {string} oldToken - Previous FCM token
   * @param {string} newToken - New FCM token
   * @param {object} deviceInfo - Updated device information
   * @returns {Promise} API response
   */
  async updateToken(oldToken, newToken, deviceInfo) {
    console.warn('updateToken() is deprecated. Remove old token and register new one instead.');
    try {
      // Remove old token first
      await this.removeToken(oldToken);
      // Register new token
      return await this.registerToken(newToken, deviceInfo);
    } catch (error) {
      console.error('Error updating token:', error);
      throw error;
    }
  }
}

export default new NotificationAPI();